import { useQuery } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CashPosition,
  InventoryStockReport,
  MarginsSummary,
  OpenRfqsResponse,
  RevenueMtd,
} from './types'

// Sprint 13b — Executive overview data hooks. Each hook owns a single
// endpoint + its caching policy. Refetch intervals match what the
// dashboard module uses for parity (1-5 min depending on volatility).

export function useRevenueMtd() {
  return useQuery({
    queryKey: ['executive', 'revenue-mtd'],
    queryFn: () => jsonFetch<RevenueMtd>('/api/reports/revenue-mtd'),
    refetchInterval: 300_000,
    staleTime: 120_000,
  })
}

export function useCashPosition() {
  return useQuery({
    queryKey: ['executive', 'cash-position'],
    queryFn: () => jsonFetch<CashPosition>('/api/reports/cash-position'),
    refetchInterval: 300_000,
    staleTime: 120_000,
  })
}

export function useOpenRfqs() {
  return useQuery({
    queryKey: ['executive', 'open-rfqs'],
    queryFn: () => jsonFetch<OpenRfqsResponse>('/api/reports/open-rfqs'),
    refetchInterval: 180_000,
    staleTime: 60_000,
  })
}

/**
 * Low stock count derived from the existing `/api/reports/inventory`
 * payload — `qty_available <= 0` matches the dashboard's filter.
 */
export function useLowStockCount() {
  return useQuery({
    queryKey: ['executive', 'low-stock'],
    queryFn: async () => {
      const data = await jsonFetch<InventoryStockReport>(
        '/api/reports/inventory?limit=500',
      )
      const products = data?.stock_report?.products ?? []
      return products.filter((p) => (p.qty_available ?? 0) <= 0).length
    },
    refetchInterval: 300_000,
    staleTime: 180_000,
  })
}

/**
 * Margin trend = current month margin_pct vs previous month margin_pct.
 * Uses the existing margins endpoint (`group_by=month` rolls up the
 * range), called twice — once for current month, once for prior. The
 * delta is computed in the frontend.
 */
export function useMarginTrend() {
  const today = new Date()
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastOfPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0)
  const firstOfPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const fmt = (d: Date) => d.toISOString().slice(0, 10)

  return useQuery({
    queryKey: [
      'executive',
      'margin-trend',
      fmt(firstOfMonth),
      fmt(lastOfPrevMonth),
    ],
    queryFn: async () => {
      const [current, previous] = await Promise.all([
        jsonFetch<MarginsSummary>(
          `/api/reports/margins?date_from=${fmt(firstOfMonth)}&date_to=${fmt(today)}`,
        ),
        jsonFetch<MarginsSummary>(
          `/api/reports/margins?date_from=${fmt(firstOfPrevMonth)}&date_to=${fmt(lastOfPrevMonth)}`,
        ),
      ])
      const currentPct = current?.summary?.margin_pct ?? null
      const previousPct = previous?.summary?.margin_pct ?? null
      const deltaPct =
        currentPct !== null && previousPct !== null
          ? Number((currentPct - previousPct).toFixed(1))
          : null
      return { currentPct, previousPct, deltaPct }
    },
    // Margins endpoint is expensive — refresh hourly.
    refetchInterval: 3_600_000,
    staleTime: 1_800_000,
  })
}
